package com.appsyde.shared.modules.invite.data.source

import com.appsyde.contract.ErrorResponseDto
import io.ktor.client.call.body
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.contentType
import io.ktor.http.isSuccess
import io.ktor.client.request.get
import io.ktor.client.request.parameter
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

@Serializable
private data class AcceptInviteRequest(val token: String)

@Serializable
private data class AcceptInviteResponse(val accessToken: String, val alreadyUsed: Boolean? = null)

@Serializable
private data class PreviewResponse(val supportInbox: String, val inviterName: String)

private val json = Json { ignoreUnknownKeys = true }

class InviteAcceptDataSourceImpl(
    private val http: io.ktor.client.HttpClient,
    private val baseUrl: String
) : InviteAcceptDataSource {

    private fun acceptUrl() = baseUrl.trimEnd('/') + "/invites/accept"
    private fun previewUrl() = baseUrl.trimEnd('/') + "/invites/preview"

    override suspend fun getPreview(token: String): InvitePreview? {
        return runCatching {
            val response = http.get(previewUrl()) { parameter("token", token) }
            if (!response.status.isSuccess()) return@runCatching null
            val body = response.body<PreviewResponse>()
            InvitePreview(supportInbox = body.supportInbox, inviterName = body.inviterName)
        }.getOrNull()
    }

    override suspend fun acceptInvite(token: String): AcceptInviteResult {
        val response = http.post(acceptUrl()) {
            contentType(ContentType.Application.Json)
            setBody(AcceptInviteRequest(token = token))
        }
        return when {
            response.status.isSuccess() -> {
                runCatching { response.body<AcceptInviteResponse>() }
                    .fold(
                        onSuccess = { body -> AcceptInviteResult.Success(body.accessToken, alreadyUsed = body.alreadyUsed == true) },
                        onFailure = { _ -> AcceptInviteResult.InvalidOrExpired }
                    )
            }
            response.status.value == 401 -> AcceptInviteResult.NeedLogin
            response.status.value == 400 -> {
                val raw = response.bodyAsText()
                val err = runCatching { json.decodeFromString<ErrorResponseDto>(raw) }.getOrNull()
                if (err?.code == "invite_already_used") AcceptInviteResult.AlreadyUsed
                else AcceptInviteResult.InvalidOrExpired
            }
            response.status.value == 403 -> AcceptInviteResult.NeedLogin
            else -> AcceptInviteResult.InvalidOrExpired
        }
    }
}
